<template>
  <v-card>
    <v-card-item v-if="year">
      <v-card-title> {{ branch.name }}: Year {{ year.year }} </v-card-title>
    </v-card-item>

    <v-card-text>
      <v-row>
        <v-col cols="2">
          <v-sheet rounded="lg">
            <v-list-item
              v-for="(item, index) in listItems"
              :key="index"
              :class="{ active: currentTab === item.name }"
              @click="selectTab(item.name)">
              {{ item.label }}
            </v-list-item>
          </v-sheet>
        </v-col>

        <v-col>
          <v-sheet min-height="70vh" rounded="lg">
            <component
              :is="currentComponent"
              v-if="currentComponent && currentProps"
              v-bind="currentProps"
              @snack="throwSnack" />
          </v-sheet>
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { SNACKSTATUS } from '@/stores/util';
import { useSnack } from '@/helpers';
import { BranchDTO, BranchYearDTO, YearDTO } from '@/dtos';
import BranchYearEditDetailSubView from './subview/BranchYearEditDetailSubView.vue';
import { useBranchYearStore } from '@/stores/modules/admin';
import BranchYearCheckInTeamsSubView from './subview/BranchYearCheckInTeamsSubView.vue';
import BranchYearAssignCriteriaSubView from './subview/BranchYearAssignCriteriaSubView.vue';

const emit = defineEmits<{
  (e: 'snack', objType: string, snackType: SNACKSTATUS, err?: string): void;
}>();

const { throwSnack } = useSnack(emit);
const route = useRoute();
const router = useRouter();
const branchYearStore = useBranchYearStore();

const yearId = ref(
  Array.isArray(route.params.yearId) ? route.params.yearId[0] : (route.params.yearId ?? ''),
);

const branchId = ref(
  Array.isArray(route.params.branchId) ? route.params.branchId[0] : (route.params.branchId ?? ''),
);

const { data } = branchYearStore.getBranchYearQuery(branchId, yearId);
const branchYear = computed(() => {
  return data.value ? new BranchYearDTO(data.value) : new BranchYearDTO(); // fallback empty
});

const branch = computed(() => {
  return branchYear.value.branch ?? new BranchDTO();
});
const year = computed(() => {
  return branchYear.value.year ?? new YearDTO();
});

const componentProps = computed(() => {
  return { yearId: yearId.value, branchId: branchId.value, branchYear: branchYear.value };
});

const listItems = computed(() => [
  {
    name: 'edit',
    label: 'Info',
    component: BranchYearEditDetailSubView,
    props: componentProps.value,
  },
  {
    name: 'checkin',
    label: 'Check In',
    component: BranchYearCheckInTeamsSubView,
    props: componentProps.value,
  },
  {
    name: 'assign-criteria',
    label: 'Assign Criteria',
    component: BranchYearAssignCriteriaSubView,
    props: componentProps.value,
  },
]);

const currentTab = ref(route.params.tabName || 'edit');

const currentTabItem = computed(() => listItems.value.find((i) => i.name === currentTab.value));

const currentComponent = computed(() => currentTabItem.value?.component || null);
const currentProps = computed(() => currentTabItem.value?.props || null);

function selectTab(tabName: string) {
  currentTab.value = tabName;
}

watch(
  currentTab,
  (newTab) => {
    router.push({
      name: 'AdminBranchYearEdit',
      params: { branchId: branchId.value, yearId: yearId.value, tabName: newTab },
    });
  },
  { immediate: true },
);
</script>
